import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ArrowLeft, MapPin, Users } from 'lucide-react';
import { CATEGORY_META } from '@/lib/utils';
import { FullPageLoader } from '@/components/Loaders';

// Public-domain US states TopoJSON (Albers USA projection)
const US_TOPO = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

function PulsingPin({ onClick, testid, plan }) {
  const isNational = plan === 'national' || plan === 'regional';
  const isLocal = plan === 'local';
  const dotColor = isNational ? '#2DE2E6' : '#FF3B3B';
  const ringColor = isNational ? 'rgba(45,226,230,0.20)' : 'rgba(255,59,59,0.16)';
  const dotR = isNational ? 6 : 4;
  const ringMax = isNational ? 18 : 14;
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }} data-testid={testid}>
      <circle r={ringMax * 0.7} fill={ringColor}>
        <animate attributeName="r" values={`5;${ringMax};5`} dur="1.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.65;0;0.65" dur="1.6s" repeatCount="indefinite" />
      </circle>
      <circle r={dotR} fill={dotColor} filter="url(#th-pin-glow)" />
      <circle r={dotR * 0.4} fill="#fff" />
      {isNational && (
        <text y={-dotR - 4} textAnchor="middle" fontSize="9" fill="#2DE2E6" fontWeight="700">★</text>
      )}
      {isLocal && (
        <circle r={dotR + 2} fill="none" stroke="#F6B24A" strokeWidth="0.8" opacity="0.6" />
      )}
    </g>
  );
}

export default function USAMapPage() {
  const nav = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/venues/map-pins').then((r) => {
      setVenues(r.data);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="map-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }} data-testid="map-title">USA Map</h1>
          <div className="ml-auto"><Logo size={96} withText={false} /></div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-3 py-4">
        <GlassCard className="p-3">
          <p className="text-sm text-white/70">
            <span className="inline-flex h-2 w-2 rounded-full bg-[#FF3B3B] th-live-dot align-middle mr-2" />
            <span data-testid="map-pin-count">{venues.length}</span> featured Thirrsty venues — every state covered. Tap a pin for details.
          </p>
          <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-white/60">
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-[#FF3B3B]" /> Listed</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-[#FF3B3B] ring-1 ring-[#F6B24A]" /> Local partner</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full bg-[#2DE2E6]" /> ★ National partner</span>
          </div>
        </GlassCard>

        {loading ? (
          <FullPageLoader label="Loading the map…" />
        ) : (
          <GlassCard className="p-2 th-noise">
            <div className="relative overflow-hidden rounded-2xl bg-[#0B0C10]">
              <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                  <filter id="th-pin-glow">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
              </svg>
              <ComposableMap
                projection="geoAlbersUsa"
                projectionConfig={{ scale: 1100 }}
                width={980}
                height={560}
                style={{ width: '100%', height: 'auto' }}
                data-testid="usa-map-svg"
              >
                <Geographies geography={US_TOPO}>
                  {({ geographies }) =>
                    geographies.map((geo) => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="rgba(246, 178, 74, 0.10)"
                        stroke="rgba(246, 178, 74, 0.45)"
                        strokeWidth={0.6}
                        style={{
                          default: { outline: 'none' },
                          hover: { fill: 'rgba(246, 178, 74, 0.18)', outline: 'none' },
                          pressed: { outline: 'none' },
                        }}
                      />
                    ))
                  }
                </Geographies>
                {venues.map((v) => (
                  <Marker key={v.id} coordinates={[v.lon, v.lat]}>
                    <PulsingPin
                      onClick={() => setSelected(v)}
                      testid={`usa-map-venue-pin-${v.id}`}
                      plan={v.subscription_plan}
                    />
                  </Marker>
                ))}
              </ComposableMap>
            </div>
          </GlassCard>
        )}
      </main>

      <Drawer open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DrawerContent className="bg-[#0B0C10] border-t border-white/10" data-testid="usa-map-venue-drawer">
          {selected && (
            <>
              <DrawerHeader>
                <DrawerTitle className="text-white" data-testid="usa-map-venue-drawer-title">{selected.name}</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-6 space-y-3">
                <img
                  src={selected.image_url}
                  onError={(e) => { e.currentTarget.src = '/logo-hero.png'; }}
                  alt={selected.name}
                  className="h-32 w-full object-cover rounded-xl"
                />
                <div className="flex items-center gap-2 text-sm text-white/80">
                  <MapPin className="h-4 w-4" />
                  {selected.city}, {selected.state}
                  <span className="ml-auto inline-flex items-center gap-1"><Users className="h-3 w-3" />{selected.check_in_count || 0}</span>
                </div>
                <p className="text-xs text-white/60">{CATEGORY_META[selected.category]?.label}</p>
                {selected.discount_text && (
                  <p className="text-xs text-[#F6B24A]">{selected.discount_text}</p>
                )}
                <Button onClick={() => nav(`/venue/${selected.id}`)} className="w-full rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]" data-testid="usa-map-venue-open-button">
                  View venue
                </Button>
              </div>
            </>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}
